import React, { Component, useCallback, useEffect, useState } from "react";
import { PorAsinListTable } from "../porAsinList/PorAsinListTable";
import AppLayoutToolbar from "@amzn/awsui-components-react/polaris/app-layout-toolbar";
import { SearchSideBar } from "../searchSideBar/searchSideBar";
import { useSelector } from "react-redux";
import { RootState } from "./rootReducer";
import Tabs from "@amzn/awsui-components-react/polaris/tabs";
import { Notification } from "../notification/notification";
import NoSsr from "./NoSsr";
import "./App.css";
import { AsinValidationTable } from "../validation/asinValidationTable";
import { BaselineTable } from "../dateBaseline/baselineTable";
import {
  addOrUpdateUrlParameter,
  getValueFromUrlParameter,
  TAB_ID_KEY,
} from "../../util/urlUtils";
import { OneClickLaunchTable } from "../oneClickLaunch/oneClickLaunchTable";
import { OclSubmissionTools } from "../oneClickLaunch/oneClickLaunchSubmissionTools";
import { CacheableNotification } from "../notification/cacheableNotification";
import { StamperWizard } from "../stampUtils/StamperWizard";
import { InitializeRum } from "../rum/initializeRum";
import { LoomAgentAssistantWidget } from "../../components/loom-agent-assistant/LoomAgentAssistantWidget";
import { selectIsLoomAgentAssistantEnabled } from "../userInfo/userInfoSlice";
import { AppShell } from "../appShell/AppShell";
import { isV2Enabled } from "./featureFlag";

const V2_PATH_PREFIX = "/v2";

/**
 * True when the current URL pathname is either `/v2` or a descendant
 * (`/v2/`, `/v2/search`, ...). `/v2foo` deliberately does NOT match.
 */
function isOnV2Path(): boolean {
  if (typeof window === "undefined") return false;
  const p = window.location.pathname;
  return p === V2_PATH_PREFIX || p.startsWith(`${V2_PATH_PREFIX}/`);
}

/**
 * Strips the `/v2` prefix and its first sub-path segment, preserving
 * `search` and `hash` so a link like `/v2/validation?tabId=dashboard#(and ...)`
 * lands on the Legacy Route with the equivalent Applied Filter.
 */
function toLegacyUrl(): string {
  const { pathname, search, hash } = window.location;
  const legacyPath = pathname.replace(/^\/v2\/?[^/]*/, "/");
  return legacyPath + search + hash;
}

const Content = () => {
  const { isEditing } = useSelector((state: RootState) => state.porAsinList);
  const isLoomAgentAssistantEnabled = useSelector(
    selectIsLoomAgentAssistantEnabled
  );
  const [tab, setTab] = useState("asins");
  const [activeDrawerId, setActiveDrawerId] = useState<string | null>(null);
  const [navigationOpen, setNavigationOpen] = useState(true);
  const tabIdFromUrl = getValueFromUrlParameter(TAB_ID_KEY, "asins");
  useEffect(() => {
    setTab(tabIdFromUrl);
  }, [tabIdFromUrl]);

  // Controlled state handlers recommended by AppLayoutToolbar docs
  // for proper toolbar toggle behavior and responsive panel management.
  const handleNavigationChange = useCallback(
    ({ detail }: { detail: { open: boolean } }) => {
      setNavigationOpen(detail.open);
    },
    []
  );

  const handleDrawerChange = useCallback(
    ({ detail }: { detail: { activeDrawerId: string | null } }) => {
      setActiveDrawerId(detail.activeDrawerId);
    },
    []
  );

  //TODO: Implement proper router after refactoring query href logic
  const stamperView = window.location.href.split("/").slice(-1)[0] == "stamper";

  return (
    <div className="awsui" data-testid="app-content-wrapper">
      <InitializeRum />
      <AppLayoutToolbar
        data-testid="app-layout"
        headerSelector={"#mosaic-navigation"}
        footerSelector={"#footer"}
        navigationOpen={navigationOpen}
        onNavigationChange={handleNavigationChange}
        navigation={
          <NoSsr>
            <SearchSideBar />
          </NoSsr>
        }
        navigationHide={isEditing || tab === "launch" || stamperView}
        contentType={"table"}
        toolsHide={true}
        notifications={
          <div data-testid="notifications-container">
            <Notification />
            <CacheableNotification />
          </div>
        }
        stickyNotifications={true}
        navigationWidth={300}
        activeDrawerId={activeDrawerId}
        onDrawerChange={handleDrawerChange}
        drawers={[
          ...(isLoomAgentAssistantEnabled
            ? [
                {
                  id: "agent-assistant",
                  ariaLabels: {
                    drawerName: "Loom Agent Assistant",
                    closeButton: "Close Loom Agent Assistant",
                    triggerButton: "Open Loom Agent Assistant",
                    resizeHandle: "Resize Loom Agent Assistant panel",
                  },
                  trigger: { iconName: "gen-ai" as const },
                  resizable: true,
                  defaultSize: 400,
                  content: (
                    <div
                      data-testid="agent-assistant-drawer"
                      style={{
                        height: "100%",
                        width: "100%",
                        boxSizing: "border-box",
                      }}
                    >
                      <LoomAgentAssistantWidget />
                    </div>
                  ),
                },
              ]
            : []),
          ...(tab === "launch" && !stamperView
            ? [
                {
                  id: "submission-history",
                  ariaLabels: {
                    drawerName: "Submission History",
                    closeButton: "Close submission history",
                    triggerButton: "Open submission history",
                    resizeHandle: "Resize submission history panel",
                  },
                  trigger: { iconName: "status-info" as const },
                  resizable: true,
                  defaultSize: 420,
                  content: (
                    <div
                      data-testid="submission-history-drawer"
                      style={{
                        padding: "20px",
                      }}
                    >
                      <OclSubmissionTools />
                    </div>
                  ),
                },
              ]
            : []),
        ]}
        content={
          <div id={"app-content"} data-testid="app-content">
            {stamperView ? (
              StamperWizard()
            ) : (
              <Tabs
                data-testid="main-tabs"
                activeTabId={tab}
                tabs={[
                  {
                    label: "ASINs",
                    id: "asins",
                    content: <PorAsinListTable />,
                  },
                  {
                    label: "Validation",
                    id: "validation",
                    content: <AsinValidationTable />,
                  },
                  {
                    label: "Baseline",
                    id: "baseline",
                    content: <BaselineTable />,
                  },
                  {
                    label: "Launch",
                    id: "launch",
                    content: <OneClickLaunchTable />,
                  },
                ]}
                onChange={(event) => {
                  const tabId = event.detail.activeTabId;
                  addOrUpdateUrlParameter(TAB_ID_KEY, tabId);
                  setTab(tabId);
                }}
              />
            )}
          </div>
        }
      />
    </div>
  );
};

/**
 * Top-level app component.
 *
 * Renders one of three trees:
 * - `<AppShell/>` when the URL is under `/v2` AND the tester has
 *   opted in (localStorage `dacw:v2Enabled === "true"`).
 * - A one-shot redirect to the equivalent Legacy path when the URL
 *   is under `/v2` but the tester has NOT opted in.
 * - The existing legacy `<Content/>` otherwise.
 *
 * The V2 gate is deliberately synchronous - the localStorage read is
 * fast enough that no loading state is needed. See `featureFlag.ts`
 * for the opt-in flow and rationale.
 */
class App extends Component {
  render() {
    if (isOnV2Path()) {
      if (isV2Enabled()) {
        return <AppShell />;
      }
      window.location.replace(toLegacyUrl());
      return null;
    }
    return <Content />;
  }
}

export default App;
